import { useEffect, useMemo, useState } from "react";
import {
  getCalculatedDataPartner,
  updateProductPartner,
} from "../../../components/products/functions";
import {
  gradingTypes,
  gradingTypeSupportsAutograph,
  normalizeGradingType,
  priorityTypes,
} from "../../../components/utils/gradingTypes";
import {
  cashnator,
  decashnator,
  isNumeric,
} from "../../../components/utils/functions";
import MobileFilterSheet from "../../ui/MobileFilterSheet";
import MobileFormField from "../../ui/MobileFormField";

const inputClass =
  "w-full min-h-[44px] rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha";

export default function MobilePartnerProductEditSheet({
  open,
  onClose,
  product,
  user,
  onSuccess,
  goodToast,
  badToast,
}) {
  const [gradingType, setGradingType] = useState("");
  const [numberOfCards, setNumberOfCards] = useState("");
  const [priority, setPriority] = useState("");
  const [deadline, setDeadline] = useState("");
  const [finalPrice, setFinalPrice] = useState("R$0,00");
  const [cardsValue, setCardsValue] = useState("R$0,00");
  const [insurance, setInsurance] = useState("R$0,00");
  const [autographEvaluation, setAutographEvaluation] = useState(false);
  const [preLacreFormat, setPreLacreFormat] = useState("with_subgrades");
  const [priceValidatedAfterChanges, setPriceValidatedAfterChanges] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !product) return;
    setGradingType(normalizeGradingType(product.gradingType) || "");
    setNumberOfCards(String(product.numberOfCards ?? ""));
    setPriority(product.priority ?? "");
    setDeadline(product.deadline ?? "");
    setFinalPrice(cashnator(product.finalPrice) || "R$0,00");
    setCardsValue(cashnator(product.cardsValue) || "R$0,00");
    setInsurance(cashnator(product.insurance));
    setAutographEvaluation(!!product.autographEvaluation);
    setPreLacreFormat(product.preLacreFormat || "with_subgrades");
    setPriceValidatedAfterChanges(true);
  }, [open, product]);

  const sensitiveChanged = useMemo(() => {
    if (!product) return false;
    return (
      normalizeGradingType(gradingType) !== normalizeGradingType(product.gradingType) ||
      `${numberOfCards}` !== `${product.numberOfCards ?? ""}` ||
      priority !== (product.priority || "") ||
      decashnator(insurance) !== (product.insurance || 0) ||
      autographEvaluation !== !!product.autographEvaluation
    );
  }, [autographEvaluation, gradingType, insurance, numberOfCards, priority, product]);

  const markPricingDirty = () => setPriceValidatedAfterChanges(false);

  const handleRecalculate = async () => {
    setBusy(true);
    const data = await getCalculatedDataPartner(
      user?.token,
      gradingType,
      numberOfCards,
      priority,
      decashnator(insurance),
      gradingTypeSupportsAutograph(gradingType) && autographEvaluation
    );
    setBusy(false);
    if (data?.type !== "success") {
      badToast("Erro ao recalcular preço e prazo");
      return;
    }
    setFinalPrice(cashnator(data?.data?.finalPrice));
    setDeadline(`${data?.data?.deadline || ""}`);
    setPriceValidatedAfterChanges(true);
    goodToast("Preço e prazo recalculados");
  };

  const handleSave = async () => {
    if (!product?._id) return;
    if (sensitiveChanged && !priceValidatedAfterChanges) {
      badToast(
        "Recalcule o preço/prazo antes de salvar alterações de tipo, autógrafo, quantidade, prioridade ou taxa de manuseio."
      );
      return;
    }
    setBusy(true);
    const body = {
      gradingType,
      numberOfCards,
      priority,
      deadline,
      finalPrice: decashnator(finalPrice),
      cardsValue: decashnator(cardsValue),
      insurance: decashnator(insurance),
      userEmail: user?.email,
      autographEvaluation:
        gradingTypeSupportsAutograph(gradingType) && autographEvaluation,
      ...(normalizeGradingType(gradingType) === "pre" ? { preLacreFormat } : {}),
    };
    const data = await updateProductPartner(user?.token, body, product._id);
    setBusy(false);
    if (data?.type !== "success") {
      badToast("Erro ao atualizar pedido");
      return;
    }
    goodToast("Pedido atualizado!");
    onSuccess();
    onClose();
  };

  const saveDisabled = busy || (sensitiveChanged && !priceValidatedAfterChanges);

  return (
    <MobileFilterSheet open={open} onClose={onClose} title="Editar pedido">
      <MobileFormField label="Tipo">
        <select
          className={inputClass}
          value={gradingType}
          onChange={(e) => {
            const next = e.target.value;
            setGradingType(next);
            if (!gradingTypeSupportsAutograph(next)) {
              setAutographEvaluation(false);
            }
            markPricingDirty();
          }}
          aria-label="Tipo de grading"
        >
          {Object.keys(gradingTypes).map((key) => (
            <option key={key} value={key}>
              {gradingTypes[key]}
            </option>
          ))}
        </select>
      </MobileFormField>

      {gradingTypeSupportsAutograph(gradingType) ? (
        <label className="mb-4 flex min-h-[44px] items-center gap-3 text-sm text-laranjinha/80">
          <input
            type="checkbox"
            checked={autographEvaluation}
            onChange={(e) => {
              setAutographEvaluation(e.target.checked);
              markPricingDirty();
            }}
            className="h-5 w-5 rounded border-primary/50"
          />
          Avaliação de autógrafo
        </label>
      ) : null}

      {normalizeGradingType(gradingType) === "pre" ? (
        <MobileFormField label="Pré — lacre">
          <select
            className={inputClass}
            value={preLacreFormat}
            onChange={(e) => setPreLacreFormat(e.target.value)}
            aria-label="Formato pré-lacre"
          >
            <option value="with_subgrades">Com subgrades</option>
            <option value="without_subgrades">Sem subgrades</option>
          </select>
        </MobileFormField>
      ) : null}

      <MobileFormField label="Nº de itens">
        <input
          className={inputClass}
          type="text"
          value={numberOfCards}
          onChange={(e) => {
            const v = e.target.value;
            if (!v || isNumeric(v)) {
              setNumberOfCards(v);
              markPricingDirty();
            }
          }}
          aria-label="Número de itens"
        />
      </MobileFormField>

      <MobileFormField label="Prioridade">
        <select
          className={inputClass}
          value={priority}
          onChange={(e) => {
            setPriority(e.target.value);
            markPricingDirty();
          }}
          aria-label="Prioridade"
        >
          {Object.keys(priorityTypes).map((key) => (
            <option key={key} value={key}>
              {priorityTypes[key]}
            </option>
          ))}
        </select>
      </MobileFormField>

      <MobileFormField label="Prazo">
        <input
          className={inputClass}
          type="text"
          value={deadline}
          onChange={(e) => {
            const v = e.target.value;
            if (!v || isNumeric(v)) setDeadline(v);
          }}
          aria-label="Prazo"
        />
      </MobileFormField>

      {product?.store?.name ? (
        <p className="mb-4 text-sm text-laranjinha/70">
          Loja: <strong>{product.store.name}</strong> · Entrega: Presencial
        </p>
      ) : null}

      <MobileFormField label="Preço final">
        <input
          className={inputClass}
          type="text"
          value={finalPrice}
          onChange={(e) => {
            const nv = decashnator(e.target.value);
            if (isNumeric(nv)) setFinalPrice(cashnator(nv));
          }}
          aria-label="Preço final"
        />
      </MobileFormField>

      <MobileFormField label="Valor itens">
        <input
          className={inputClass}
          type="text"
          value={cardsValue}
          onChange={(e) => {
            const nv = decashnator(e.target.value);
            if (isNumeric(nv)) setCardsValue(cashnator(nv));
          }}
          aria-label="Valor itens"
        />
      </MobileFormField>

      <MobileFormField label="Taxa de manuseio">
        <input
          className={inputClass}
          type="text"
          value={insurance}
          onChange={(e) => {
            const nv = decashnator(e.target.value);
            if (isNumeric(nv)) {
              setInsurance(cashnator(nv));
              markPricingDirty();
            }
          }}
          aria-label="Taxa de manuseio"
        />
      </MobileFormField>

      {sensitiveChanged && !priceValidatedAfterChanges ? (
        <p className="mb-4 text-center text-sm text-amber-600">
          Campos sensíveis foram alterados. Recalcule preço/prazo antes de salvar.
        </p>
      ) : null}

      <button
        type="button"
        disabled={busy || !sensitiveChanged}
        onClick={handleRecalculate}
        className="mb-3 inline-flex min-h-[44px] w-full items-center justify-center rounded-lg border border-amber-500 bg-amber-50 font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
      >
        Recalcular preço/prazo
      </button>

      <button
        type="button"
        disabled={saveDisabled}
        onClick={handleSave}
        className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-primary font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50"
      >
        {busy ? "Salvando…" : "Salvar alterações"}
      </button>
    </MobileFilterSheet>
  );
}
